import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import useCarHireReservationInfo from 'hooks/CarHire/useCarHireReservationInfo'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import CarHireLocationModalSkeleton from './CarHireLocationModalSkeleton'
import { sortBy } from 'lib/array/arrayUtils'
import { mod } from 'lib/maths/mathUtils'
import type { CarHireLocationTabId } from 'components/CarHire/carHireLocationTabs'
import CarHireLocationModalContent from 'components/CarHire/CarHireLocationModalContent'
import { shortTimeHourFormatAmPm } from 'lib/datetime/dateUtils'

interface OperatingTime {
  dayStart: string
  hours: string
}

function mapOperatingTimeToLabel(
  operatingTime: App.CarHireLocationOperatingTime,
  closedLabel: string,
  intl: IntlShape,
): OperatingTime {
  if (!operatingTime.start && !operatingTime.end) {
    return {
      dayStart: operatingTime.day,
      hours: closedLabel,
    }
  }

  return {
    dayStart: operatingTime.day,
    hours: intl.formatMessage(
      {
        id: 'SA1a17YLGYxbNoIT23a3',
        defaultMessage: '{startTime} - {endTime}',
        description: 'Operating time range for the car hire pick-up or drop-off location modal',
      },
      {
        startTime: shortTimeHourFormatAmPm(new Date(`1970-01-01T${operatingTime.start}:00Z`), true),
        endTime: shortTimeHourFormatAmPm(new Date(`1970-01-01T${operatingTime.end}:00Z`), true),
      },
    ),
  }
}

function getOpeningGroups(
  location: App.CarHireReservationLocation,
  closedLabel: string,
  intl: IntlShape,
) {
  const operatingTimes = location.operatingHours.map((operatingTime) => (
    mapOperatingTimeToLabel(operatingTime, closedLabel, intl)
  ))

  // 0 is usually sunday, -1 shifts 0 to begin monday (*our* first day)
  return sortBy(operatingTimes, time => mod((new Date(time.dayStart).getDay() - 1), 7), 'asc')
}

interface Props {
  openTab: CarHireLocationTabId
  orderItem: App.OrderCarHireItem
}

function CarHireItemSummaryLocationModal(props: Props) {
  const intl = useIntl()
  const { openTab, orderItem } = props
  const closedLabel = intl.formatMessage({
    id: '7bgAuTU4K0qNnS91aG5H',
    defaultMessage: 'closed',
    description: 'Label shown when car hire location is closed on a given day',
  })

  const reservationId = orderItem.idReservation || ''

  const reservationInfo = useCarHireReservationInfo(reservationId).reservation

  const pickUpLocation = reservationInfo?.pickUp
  const dropOffLocation = reservationInfo?.dropOff
  const differentLocations = pickUpLocation?.id !== dropOffLocation?.id
  const title = differentLocations
    ? intl.formatMessage({
        id: 'LRu5-mZWfO-lZvKjWM1J',
        defaultMessage: 'Pick-up & Drop-off location',
        description: 'Modal title when car hire pick-up and drop-off are at different locations',
      })
    : intl.formatMessage({
        id: 'JhZ2KriBWExpI11exLMi',
        defaultMessage: 'Pick-up & Drop-off locations',
        description: 'Modal title when car hire pick-up and drop-off are at the same location',
      })

  const pickUpLocationContent = useMemo(() => {
    if (pickUpLocation?.latitude == null || pickUpLocation.longitude == null) {
      return undefined
    }

    return {
      id: pickUpLocation.id,
      name: pickUpLocation.locationName,
      address: pickUpLocation.address ?? '',
      marker: {
        id: `${pickUpLocation.id}-pick-up`,
        type: PROPERTY_MAP_MARKER_TYPE,
        latitude: pickUpLocation.latitude,
        longitude: pickUpLocation.longitude,
      } satisfies App.MapMarker,
      openingGroups: getOpeningGroups(pickUpLocation, closedLabel, intl),
      pickupType: pickUpLocation.type,
    }
  }, [closedLabel, intl, pickUpLocation])

  const dropOffLocationContent = useMemo(() => {
    if (dropOffLocation?.latitude == null || dropOffLocation.longitude == null || !pickUpLocation) {
      return undefined
    }

    return {
      id: dropOffLocation.id,
      name: dropOffLocation.locationName,
      address: dropOffLocation.address ?? '',
      marker: {
        id: `${dropOffLocation.id}-drop-off`,
        type: PROPERTY_MAP_MARKER_TYPE,
        latitude: dropOffLocation.latitude,
        longitude: dropOffLocation.longitude,
      } satisfies App.MapMarker,
      openingGroups: differentLocations
        ? getOpeningGroups(dropOffLocation, closedLabel, intl)
        : getOpeningGroups(pickUpLocation, closedLabel, intl),
      pickupType: dropOffLocation.type,
    }
  }, [closedLabel, differentLocations, dropOffLocation, intl, pickUpLocation])

  const hasContent = pickUpLocation && dropOffLocation && pickUpLocationContent && dropOffLocationContent

  return <ModalBase>
    <ModalHeader title={title} />
    <ModalBody>
      {!hasContent && <CarHireLocationModalSkeleton />}
      {hasContent &&
        <CarHireLocationModalContent
          pickUpLocation={pickUpLocationContent}
          dropOffLocation={dropOffLocationContent}
          openTab={openTab}
        />}
    </ModalBody>
  </ModalBase>
}

export default CarHireItemSummaryLocationModal
